/**
 * プランマスタ定数
 */

import { PlanDefinition, PaymentCycle, PlanPricing } from '../types/plan';
import { companyPlan, geminiModel, paymentCycle } from './enum';

// Gemini 2.5 Pro 使用時の消費単価（円/枚）
const OCR_CREDIT_RATE_PRO_MODEL_HIGH = 80; // フリー・スタンダードプラン
const OCR_CREDIT_RATE_PRO_MODEL_LOW = 30; // プロ・オーダーメードプラン

/**
 * プランマスタ定義
 * キー: companyPlan（0=free, 1=standard, 2=pro, 3=custom）
 */
export const PLANS: Record<number, PlanDefinition> = {
  [companyPlan.free]: {
    planCode: 0,
    name: 'フリープラン',
    nameEn: 'Free',
    displayOrder: 1,
    limits: {
      ocrMonthlyBudget: 250,
      userCount: 1,
      patternCount: 1, // フリープランの読取パターン上限（enum.planLimits.free.patternLimit と一致させる）
    },
    additionalOcr: {
      enabled: false,
      pricePerOcr: 50, // 運営手動付与時のみ使用
    },
    additionalUser: {
      enabled: false,
    },
    accuracySupport: {
      enabled: false,
      price: 0,
    },
    pricing: {
      none: {
        monthlyPrice: 0,
        additionalUserPrice: 0,
        initialCost: 0,
        minimumTerm: 12,
      },
    },
    isActive: true,
  },

  [companyPlan.standard]: {
    planCode: 1,
    name: 'スタンダード',
    nameEn: 'Standard',
    displayOrder: 2,
    limits: {
      ocrMonthlyBudget: 5000,
      userCount: 10,
      patternCount: 100,
    },
    additionalOcr: {
      enabled: true,
      pricePerOcr: 50,
    },
    additionalUser: {
      enabled: true,
    },
    accuracySupport: {
      enabled: false,
      price: 0,
    },
    pricing: {
      // 月払いは年契約月額の20%増し（#2833）。最低契約期間は1ヶ月
      monthly: {
        monthlyPrice: 12500,
        additionalUserPrice: 1250,
        initialCost: 0,
        minimumTerm: 1,
      },
      yearly: {
        monthlyPrice: 10000,
        additionalUserPrice: 1000,
        initialCost: 0,
        minimumTerm: 12,
      },
    },
    isActive: true,
  },

  [companyPlan.pro]: {
    planCode: 2,
    name: 'プロ',
    nameEn: 'Pro',
    displayOrder: 3,
    limits: {
      ocrMonthlyBudget: 5000,
      userCount: 10,
      patternCount: 100,
    },
    additionalOcr: {
      enabled: true,
      pricePerOcr: 15,
    },
    additionalUser: {
      enabled: true,
    },
    accuracySupport: {
      enabled: true,
      price: 200000,
    },
    pricing: {
      // 月払いは年契約月額の20%増し（#2833）。最低契約期間は1ヶ月
      monthly: {
        monthlyPrice: 30000,
        additionalUserPrice: 3000,
        initialCost: 0,
        minimumTerm: 1,
      },
      yearly: {
        monthlyPrice: 24000,
        additionalUserPrice: 2400,
        initialCost: 0,
        minimumTerm: 12,
      },
    },
    isActive: true,
  },

  [companyPlan.custom]: {
    planCode: 3,
    name: 'オーダーメード',
    nameEn: 'Custom',
    displayOrder: 4,
    limits: {
      ocrMonthlyBudget: 13500,
      userCount: 30,
      patternCount: 100,
    },
    additionalOcr: {
      enabled: true,
      pricePerOcr: 15,
    },
    additionalUser: {
      enabled: true,
    },
    accuracySupport: {
      enabled: true,
      price: 200000,
    },
    pricing: {
      yearly: {
        monthlyPrice: 90000,
        additionalUserPrice: 3000,
        initialCost: 1200000,
        minimumTerm: 12,
      },
    },
    isActive: true,
  },
};

/**
 * プランコードからプラン情報を取得
 * @param planCode - プランコード（0-3）
 * @returns プラン定義 or undefined
 */
export const getPlanByCode = (planCode: number): PlanDefinition | undefined => {
  return PLANS[planCode];
};

/**
 * プラン表示名を取得
 * @param planCode - プランコード（0-3）
 * @returns プラン名（例: "スタンダード"）
 */
export const getPlanLabel = (planCode: number): string => {
  return PLANS[planCode]?.name ?? '不明なプラン';
};

/**
 * プラン表示名（英語）を取得
 * @param planCode - プランコード（0-3）
 * @returns プラン名英語（例: "Standard"）
 */
export const getPlanLabelEn = (planCode: number): string => {
  return PLANS[planCode]?.nameEn ?? 'Unknown';
};

/**
 * 支払いサイクル別料金を取得
 * @param planCode - プランコード（0-3）
 * @param paymentCycle - 支払いサイクル（none/monthly/yearly）
 * @returns 料金情報 or undefined
 */
export const getPlanPricing = (
  planCode: number,
  paymentCycle: PaymentCycle,
): PlanPricing | undefined => {
  return PLANS[planCode]?.pricing[paymentCycle];
};

/**
 * 全ての有効プラン一覧を取得
 * @returns プラン定義の配列（displayOrder順）
 */
export const getAllActivePlans = (): PlanDefinition[] => {
  return Object.values(PLANS)
    .filter(plan => plan.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder);
};

/**
 * 画面で選択可能なプラン一覧を取得（オーダーメードを除外）
 * オーダーメードはSaaSではなく独自環境のため選択不可
 * @returns プラン定義の配列（displayOrder順）
 */
export const getSelectablePlans = (): PlanDefinition[] => {
  return getAllActivePlans().filter(plan => plan.planCode !== companyPlan.custom);
};

/**
 * プランコードに対応するデフォルトの支払いサイクルを取得
 * フリー → none（0）、有料プラン → yearly（2）
 * ※有料プランの初期表示は年払いのまま（#2833 で確定）。
 *   月払いは料金マスタに定義済みで、画面のラジオボタンから選択可能。
 * @param planCode - プランコード（0-3）
 * @returns 支払いサイクル値（数値）
 */
export const getDefaultPaymentCycle = (planCode: number): number => {
  if (planCode === companyPlan.free) {
    return paymentCycle.none;
  }
  return paymentCycle.yearly;
};

/**
 * OCRクレジット消費単価を取得
 * @param planCode - プランコード（0-3）
 * @returns 消費単価（円/枚）
 */
export const getOcrCreditRate = (planCode: number): number => {
  return PLANS[planCode]?.additionalOcr.pricePerOcr ?? 50;
};

/**
 * モデルとプランを考慮したOCRクレジット消費単価を取得
 * Gemini 2.5 Pro 使用時はプランに応じて単価が異なる
 * @param planCode - プランコード（0-3）
 * @param geminiModelValue - Geminiモデルのenum値
 * @returns 消費単価（円/枚）
 */
export const getOcrCreditRateWithModel = (planCode: number, geminiModelValue: number): number => {
  if (geminiModelValue === geminiModel.pro_2_5) {
    return planCode === companyPlan.pro || planCode === companyPlan.custom
      ? OCR_CREDIT_RATE_PRO_MODEL_LOW
      : OCR_CREDIT_RATE_PRO_MODEL_HIGH;
  }
  return getOcrCreditRate(planCode);
};

/**
 * プランの利用制限を取得
 * @param planCode - プランコード（0-3）
 * @returns 利用制限 or undefined
 */
export const getPlanLimits = (planCode: number): PlanDefinition['limits'] | undefined => {
  return PLANS[planCode]?.limits;
};

/**
 * 追加OCRが可能かどうかを判定
 * @param planCode - プランコード（0-3）
 * @returns true: 追加OCR可能
 */
export const canPurchaseAdditionalOcr = (planCode: number): boolean => {
  return PLANS[planCode]?.additionalOcr.enabled ?? false;
};

/**
 * 追加ユーザーが可能かどうかを判定
 * @param planCode - プランコード（0-3）
 * @returns true: 追加ユーザー可能
 */
export const canAddUser = (planCode: number): boolean => {
  return PLANS[planCode]?.additionalUser.enabled ?? false;
};

/**
 * 精度アップ支援が可能かどうかを判定
 * @param planCode - プランコード（0-3）
 * @returns true: 精度アップ支援可能
 */
export const canUseAccuracySupport = (planCode: number): boolean => {
  return PLANS[planCode]?.accuracySupport.enabled ?? false;
};

/**
 * 月間OCR枠（円）から枚数目安を算出
 * @param planCode - プランコード（0-3）
 * @returns 枚数目安（小数点以下切り捨て）
 */
export const getOcrMonthlyPages = (planCode: number): number => {
  const plan = PLANS[planCode];
  if (!plan) return 0;
  return Math.floor(plan.limits.ocrMonthlyBudget / plan.additionalOcr.pricePerOcr);
};
